import { FeatureHelperService } from './../_helpers/feature-helper/feature-helper.service';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { ThemeService } from '../_helpers/theming-engine/theme.service';
import { AuthenticationService } from '../_services';
import { ActivatedRoute, Router } from '@angular/router';
import { MsalService } from '@azure/msal-angular';
import { InteractionRequiredAuthError } from '@azure/msal-browser';
import { EMPTY, Observable, of, throwError } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { ErrorModalHelperService } from '../_helpers/error-modal-helper/error-modal-helper.service';
import { AuthenticationStateHelperService } from '../_helpers/authentication-state-helper/authentication-state-helper.service';
import { SuccessModalHelperService } from '../_helpers/success-modal-helper/success-modal-helper.service';

@Component({
    selector: 'ad-login',
    templateUrl: './ad-login.component.html',
    styleUrls: ['./ad-login.component.css'],
    standalone: false
})
export class AdLoginComponent implements OnInit, OnDestroy {

  private returnUrl: string = "/";

  // The manual Sign In button is a fallback for when the automatic redirect to Entra never starts
  // (e.g. an MSAL interaction is already in progress). It stays disabled for the first 5 seconds so the
  // normal auto-redirect has time to take the user away; if we are still on this page after that, the
  // redirect has effectively failed and we let the user trigger it by hand.
  signInEnabled = false;
  private enableButtonTimer: ReturnType<typeof setTimeout>;

  constructor(
    private route: ActivatedRoute,
    private msalService: MsalService,
    private router: Router,
    public themeService: ThemeService,
    private featureHelperService: FeatureHelperService,
    private authenticationService: AuthenticationService,
    private successModalHelper: SuccessModalHelperService,
    private errorModalHelper: ErrorModalHelperService,
    private authenticationStateHelper: AuthenticationStateHelperService
    ) { }

  ngOnInit() {
    const companyType = 'LEAP';
    this.themeService.setActiveTheme(companyType, false);

    // Wall-clock fallback: if the auto-redirect below hasn't navigated us away within 5 seconds,
    // enable the manual Sign In button. A successful redirect leaves the page, so this never fires.
    this.enableButtonTimer = setTimeout(() => { this.signInEnabled = true; }, 5000);

    this.msalService.handleRedirectObservable().subscribe(result => {
      // Default to '/' — on the MSAL redirect-return there is no returnUrl query param, and if the
      // AuthenticationStateHelper has none either this was undefined, which later became a null route
      // segment in router.navigate and threw NG04008 ("path contains undefined segment").
      this.returnUrl = this.route.snapshot.queryParams['returnUrl'] || this.authenticationStateHelper.returnUrl || '/';
      let returnUrlCheck = sessionStorage.getItem("login-redirect");
      if (!returnUrlCheck || returnUrlCheck == '/') {
        sessionStorage.setItem("login-redirect", this.returnUrl);
      }

      if (this.authenticationStateHelper.unauthorizedToken) {
        this.errorModalHelper.show("", {status: 401});
        this.authenticationStateHelper.unauthorizedToken = false;
      }

      // ?prompt=select_account (set by the login page's "sign in as a different account" checkbox)
      // forces the Microsoft account picker even when an account is already cached — otherwise the
      // cached-account branch below would silently reuse the current identity and you could never
      // switch. Only on a fresh visit (result === null); the picker's redirect-return then flows
      // through the normal path.
      const promptParam = this.route.snapshot.queryParams['prompt'];
      if (result === null && promptParam === 'select_account') {
        this.login('select_account');
        return;
      }

      // Prefer the account that just authenticated on a redirect-return (result.account) — after a
      // select_account the cache holds more than one, so getAllAccounts()[0] could be the wrong one.
      const account = result?.account ?? this.msalService.instance.getAllAccounts()[0];
      if (account) {
        const userName = account.username;

        // localAccountId IS the token's `oid` — Entra's immutable object id for this account. Send it
        // so the API keys the lookup on identity rather than on address: account.username is either
        // the home UPN or the #EXT# guest form depending on how the person authenticated, and the two
        // forms of one B2B guest used to resolve to two separate LEAP users.
        const entraObjectId = account.localAccountId;

        // The API signs in only on a Microsoft ID token it can validate. A redirect-return carries a
        // fresh one; a cached account's may have expired (they last an hour), so ask MSAL for a new
        // one with the refresh token it holds. If that needs the user, send them to Microsoft again.
        const idToken$: Observable<string> = result?.idToken
          ? of(result.idToken)
          : this.msalService.acquireTokenSilent({ scopes: ['user.read'], account, forceRefresh: true })
              .pipe(map(silent => silent.idToken));

        idToken$.pipe(
          catchError(silentError => {
            if (silentError instanceof InteractionRequiredAuthError) {
              this.login();
              return EMPTY;
            }
            return throwError(() => silentError);
          }),
          switchMap(idToken => this.authenticationService.authenticateAdUser(userName, companyType, entraObjectId, idToken))
        ).subscribe((data: any) => {
          this.featureHelperService.setUserFeatures(data.userFeatures);
          this.authenticationService.setUser(data);
          let returnUrlHolder = sessionStorage.getItem("login-redirect");
          sessionStorage.removeItem("login-redirect");
          // Guard against null/'undefined'/'null' — router.navigate([null]) throws NG04008 and the
          // sign-in appears to fail even though authentication succeeded.
          if (!returnUrlHolder || returnUrlHolder === 'undefined' || returnUrlHolder === 'null') {
            returnUrlHolder = '/';
          }

          setTimeout(() => {
            this.themeService.setActiveTheme(companyType);
            this.router.navigateByUrl(returnUrlHolder);
          }, 100);
        },
        error => {
          this.errorModalHelper.show(error.message, error);
        });
      } else if (result === null) {
        // Fresh visit (result === null means we are NOT returning from a redirect) with no cached
        // account: go straight to Entra instead of rendering the landing page with a manual "Sign In"
        // button. Without this, /signmein is an extra page the user must click through before the
        // Entra challenge. Guarded on result === null so the redirect-return pass (result populated,
        // account then present) never re-initiates a login and loops. The button stays as a fallback
        // for the rare case a redirect cannot start (e.g. interaction already in progress).
        this.login();
      }
    });
  }

  ngOnDestroy(): void {
    clearTimeout(this.enableButtonTimer);
  }

  public login(prompt?: string): void {
    // prompt is undefined for the normal (silent-capable) sign-in and 'select_account' when the user
    // explicitly wants the account picker. Passing undefined is a no-op, so MSAL uses its default.
    this.msalService.loginRedirect({ scopes: ['user.read'], prompt: prompt as any });
  }

}
